export interface Slide {
  src: string;
  lazy?: boolean;
  badge: {
    en: string;
    id: string;
  };
  title: {
    en: string;
    id: string;
  };
  subtitle: {
    en: string;
    id: string;
  };
  ctaLabel?: {
    en: string;
    id: string;
  };
  ctaLink?: string;
  hidePrimaryCta?: boolean;
}

export interface GalleryItem {
  id: string;
  code: string;
  image: string;
  title: string;
  category: string;
  description: string;
}

export interface Review {
  reviewId: string;
  displayName: string;
  profilePhotoUrl: string;
  starRating: number;
  comment: string;
  createTime: string;
}

export interface SiteConfig {
  phoneNumberTony: string;
  phoneNumberNila: string;
  whatsappMessageTonyEn: string;
  whatsappMessageTonyId: string;
  whatsappMessageNilaEn: string;
  whatsappMessageNilaId: string;
  googleRating: number;
  googleReviewCount: number;
  slides: Slide[];
  galleryItems: GalleryItem[];
  reviews: Review[];
}

// Dynamically generate default gallery items to preserve original asset listings
const defaultGalleryItems: GalleryItem[] = [
  // Wedding Cakes (1-17)
  ...Array.from({ length: 17 }, (_, i) => ({
    id: `wedding-${i + 1}`,
    code: `WED-${String(i + 1).padStart(3, '0')}`,
    image: `/tncakes/WeddingCakes/WeddingCakes${i + 1}.jpeg`,
    title: `Wedding Cake #${i + 1}`,
    category: 'wedding',
    description: ''
  })).reverse(),
  // Birthday Cakes (1-153)
  ...Array.from({ length: 153 }, (_, i) => ({
    id: `birthday-${i + 1}`,
    code: `BTH-${String(i + 1).padStart(3, '0')}`,
    image: `/tncakes/BirthdayCakes/BirthdayCakes${i + 1}.jpeg`,
    title: `Birthday Cake #${i + 1}`,
    category: 'birthday',
    description: ''
  })).reverse(),
  // Cupcakes (1-27)
  ...Array.from({ length: 27 }, (_, i) => ({
    id: `cupcakes-${i + 1}`,
    code: `CUP-${String(i + 1).padStart(3, '0')}`,
    image: `/tncakes/Cupcakes/Cupcakes${i + 1}.jpeg`,
    title: `Cupcake #${i + 1}`,
    category: 'cupcakes',
    description: ''
  })).reverse(),
  // Corporate Cakes (1-5)
  ...Array.from({ length: 5 }, (_, i) => ({
    id: `corporate-${i + 1}`,
    code: `COR-${String(i + 1).padStart(3, '0')}`,
    image: `/tncakes/CorporateCakes/CorporateCakes${i + 1}.jpeg`,
    title: `Corporate Cake #${i + 1}`,
    category: 'corporate',
    description: ''
  })).reverse(),
  // Celebration Cakes (1-10)
  ...Array.from({ length: 10 }, (_, i) => ({
    id: `celebration-${i + 1}`,
    code: `CEL-${String(i + 1).padStart(3, '0')}`,
    image: `/tncakes/CelebrationCakes/CelebrationCakes${i + 1}.jpeg`,
    title: `Celebration Cake #${i + 1}`,
    category: 'celebration',
    description: ''
  })).reverse(),
];

export const DEFAULT_SITE_CONFIG: SiteConfig = {
  phoneNumberTony: '+61 401 461 019',
  phoneNumberNila: '+62 813 3732 9299',
  whatsappMessageTonyEn: "Hello Tony! I would like to enquire about ordering a custom cake from TN Cakes Bali®",
  whatsappMessageTonyId: "Halo Tony! Saya ingin bertanya tentang pemesanan kue custom dari TN Cakes Bali®",
  whatsappMessageNilaEn: "Hello Nila! I would like to enquire about ordering a custom cake from TN Cakes Bali®",
  whatsappMessageNilaId: "Halo Nila! Saya ingin bertanya tentang pemesanan kue custom dari TN Cakes Bali®",
  googleRating: 5.0,
  googleReviewCount: 567,
  slides: [
    {
      src: '/tncakes/CorporateCakes/CorporateCakes5.jpeg',
      lazy: false,
      badge: { en: 'Online Service', id: 'Layanan Online' },
      title: { en: 'Online Premium\nOrders', id: 'Pemesanan Premium\nOnline' },
      subtitle: { en: 'We accept premium custom cake orders online only', id: 'Kami hanya menerima pesanan kue custom premium secara online' }
    },
    {
      src: '/tncakes/BirthdayCakes/BirthdayCakes65.jpeg',
      lazy: false,
      badge: { en: 'Signature Collection', id: 'Koleksi Khas' },
      title: { en: 'Premium Cakes', id: 'Kue Premium' },
      subtitle: { en: 'Experience the luxury of quality cakes with unique designs crafted with love and high dedication.', id: 'Rasakan kemewahan kue berkualitas dengan desain unik yang dibuat dengan cinta dan dedikasi tinggi.' }
    },
    {
      src: '/tncakes/BirthdayCakes/BirthdayCakes46.jpeg',
      lazy: true,
      badge: { en: 'Birthday Special', id: 'Spesial Ulang Tahun' },
      title: { en: 'Birthday Cakes\nThat Wow', id: 'Kue Ulang Tahun\nYang Memukau' },
      subtitle: { en: 'Make your celebration special with spectacular birthday cakes that will amaze all your guests.', id: 'Buat perayaan Anda istimewa dengan kue ulang tahun spektakuler yang akan memukau semua tamu Anda.' }
    },
    {
      src: '/tncakes/WeddingCakes/WeddingCakes3.jpeg',
      lazy: true,
      badge: { en: 'Wedding Dreams', id: 'Pernikahan Impian' },
      title: { en: 'Dream Wedding\nCakes', id: 'Kue Pernikahan\nImpian' },
      subtitle: { en: 'Create magical moments with stunning wedding cakes designed to match your dream celebration.', id: 'Ciptakan momen ajaib dengan kue pernikahan menakjubkan yang dirancang sesuai dengan perayaan impian Anda.' }
    },
    {
      src: '/tncakes/Cupcakes/Cupcakes27.jpeg',
      lazy: true,
      badge: { en: 'Sweet Cupcakes', id: 'Cupcake Manis' },
      title: { en: 'Perfect\nCupcakes', id: 'Cupcake\nSempurna' },
      subtitle: { en: 'Bring happiness in small bites with quality cupcakes perfect for every occasion.', id: 'Bawa kebahagiaan dalam setiap gigitan kecil dengan cupcake berkualitas yang sempurna untuk setiap kesempatan.' }
    },
    {
      src: '/tncakes/CorporateCakes/CorporateCakes3.jpeg',
      lazy: true,
      badge: { en: 'For Business', id: 'Untuk Bisnis' },
      title: { en: 'For Business\nPartnerships', id: 'Untuk Kemitraan\nBisnis' },
      subtitle: { en: 'Tailored cakes and desserts designed to impress your guests and elevate your corporate image. Perfect for hotels, restaurants, cafés, and events.', id: 'Kue dan pencuci mulut yang disesuaikan untuk mengesankan tamu Anda dan meningkatkan citra perusahaan Anda. Sempurna untuk hotel, restoran, kafe, dan acara.' },
      ctaLabel: { en: 'For Business', id: 'Untuk Bisnis' },
      ctaLink: '/for-business',
      hidePrimaryCta: true
    },
    {
      src: '/tncakes/BirthdayCakes/BirthdayCakes70.jpeg',
      lazy: true,
      badge: { en: 'Halal Certified', id: 'Sertifikasi Halal' },
      title: { en: 'Halal Certified', id: 'Bersertifikat Halal' },
      subtitle: { en: 'We are committed to providing halal-certified cakes with the highest quality ingredients and standards.', id: 'Kami berkomitmen untuk menyediakan kue bersertifikat halal dengan bahan dan standar kualitas tertinggi.' },
      ctaLabel: { en: 'Learn More', id: 'Pelajari Selengkapnya' },
      ctaLink: '/about',
      hidePrimaryCta: true
    }
  ],
  galleryItems: defaultGalleryItems,
  reviews: [
    {
      reviewId: '1',
      displayName: 'April Situ',
      profilePhotoUrl: 'https://lh3.googleusercontent.com/geougc-cs/ABOP9psGxajuprYDyFnp-VW4R0_-GGa3yaorshA4aO-fa4Nob85wN5_oZauwtQOG9L58_JQ1SJETJnxJzDFFJQsglt6m9gdjvQYo4bFogej8VlObR4wQD_w-76A7sAsE6S7E7Hoo4Kib0AmOTjUd=s500-p-k',
      starRating: 5,
      comment: 'Tony was unbelievably helpful and accomodating, I placed a very last minute order and we were able to plan/receive the cake the very next day! The communication was super quick and easy, given the short timeframe, tony and their team were able to pull something together quickly whilst maintaining quality. We were able to organise for the cake to be delivered to our villa so I could surprise my partner. The cake itself was super soft and tasty, my partner and our friends loved it - highly highly recommend ☺️',
      createTime: '2026-02-18'
    },
    {
      reviewId: '2',
      displayName: 'Fiona Mooney',
      profilePhotoUrl: 'https://lh3.googleusercontent.com/geougc-cs/ABOP9ptoj1_XDF_JBQAr6wk4WjLLOi6bMVTWyvqpix6jJLLZx68rKhCcizC2ZOdyeZoua9IF8GlRAd7kwrGGdZPWUkTc8xcS6pnVBronFQJskw-puos4yyBN4ZFWfBG7tUBscIziaEOIsHy-Gu0W=s500-p-k',
      starRating: 5,
      comment: 'From the moment I contacted them there was excellent responsiveness and great care taken to ensure my daughter cake was exactly what she wanted. In fact it was way above expectations in taste, presentation and decorations. They hand made lilly\'s which were just amazing, everyone at the party took the details of this amazing cake company. Wont look anywhere else again and will be contacting them for 2 more family parties I have this year.',
      createTime: '2026-02-17'
    },
    {
      reviewId: '3',
      displayName: 'Sandra Tan',
      profilePhotoUrl: 'https://lh3.googleusercontent.com/geougc-cs/ABOP9ptfJAxX-TjdX0oU1ZMHJFuUaZp5Kq_lS1XXxuy7FUxV87a5ITNeIKT_OcYYPk4PQ3wDZVjuFb5_N8RyRhsc1uHjRfoJ2z6lDIFFN8sXe5sEwLx55WuqzE9ec4NkgH6_8a_UlaPhHOq7FFod=s500-p-k',
      starRating: 5,
      comment: 'For my friend’s bridesmaid bali trip, we decided to get a cake. Chanced upon TN Cakes and communicated with Antonio on the customised cake design and flavours. WE LOVED IT! Freshly baked and Antonio even went the extra mile to help me arrange for delivery. Thanks so much for being part of this memorable trip!',
      createTime: '2026-02-17'
    },
    {
      reviewId: '4',
      displayName: 'David Chen',
      profilePhotoUrl: '/default-profile.png',
      starRating: 5,
      comment: 'Outstanding quality and customer service. The custom design process was smooth and the final result was beyond our expectations.',
      createTime: '2024-07-05'
    },
    {
      reviewId: '5',
      displayName: 'Priya Sharma',
      profilePhotoUrl: '/default-profile.png',
      starRating: 5,
      comment: 'The most delicious and beautifully designed cakes in Bali! Worth every penny for such premium quality.',
      createTime: '2024-06-28'
    },
    {
      reviewId: '6',
      displayName: 'Michael Thompson',
      profilePhotoUrl: '/default-profile.png',
      starRating: 5,
      comment: 'Exceptional service from start to finish. The team understood our vision perfectly and delivered a masterpiece.',
      createTime: '2024-06-22'
    }
  ]
};
